import { Component, OnInit } from '@angular/core';
import { EMPTY, expand, finalize, forkJoin } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { ChartDataset } from 'chart.js';
import moment from 'moment';
import { orderBy } from 'lodash';

import { EventEmitter, Output } from '@angular/core';
import { ChartDataPoint } from 'src/app/core/entities/chart-data-point.entity';
import { ChartEvaluation } from 'src/app/core/entities/chart-evaluation';
import { ChartLabel } from 'src/app/core/entities/chart-dataset';
import { ChartService } from 'src/app/core/services/chart.service';
import { EvaluationService } from 'src/app/core/services/evaluation.service';
import { Feature } from 'src/app/core/entities/feature.entity';
import { FeatureService } from 'src/app/core/services/feature.service';

@Component({
  selector: 'app-surveys',
  templateUrl: './surveys.component.html',
  styleUrls: ['./surveys.component.css'],
})
export class SurveysComponent implements OnInit {
  private _features: any = {};
  public evaluations = {
    0: <ChartEvaluation>{
      evaluationCode: EvaluationService.EVALUATION_BLCS,
      title: $localize`:@@patient_evaluationOverviewSurveys_evaluationBLCSTitle:Bladder Control Scale (BLCS)`,
      titleTile: $localize`:@@patient_evaluationOverviewSurveys_evaluationBLCSTitleTile:Échelle de Contrôle de la Vessie (BLCS)`,
      order: 0,
      features: [
        {
          featureId: FeatureService.FEATURE_BLCS_SCR,
          title: $localize`:@@patient_evaluationOverviewSurveys_featureBLCSSCRTitle:Score`,
          label: $localize`:@@patient_evaluationOverviewSurveys_featureBLCSSCRLabel:Score`,
          min: 0,
          max: 22,
          chartDatasetOption: {
            type: 'bar',
            color: '#8893aa',
            nbSteps: 10,
            tooltip: {
              label: $localize`:@@patient_evaluationOverviewSurveys_featureBLCSSCRTooltipLabel:Score`,
              unit: '',
            },
          },
        },
      ],
      horizontalLimitLines: [
        {
          label: $localize`:@@patient_evaluationOverviewSurveys_featureHorizontalLimitLineYLabel:Maximum severity`,
          color: '#8893aa',
          assignToAxisIds: {
            y: 22,
          },
        },
      ],
    },
    1: <ChartEvaluation>{
      evaluationCode: EvaluationService.EVALUATION_BWCS,
      title: $localize`:@@patient_evaluationOverviewSurveys_evaluationBWCSTitle:Bowel Check Scale (BWCS)`,
      titleTile: $localize`:@@patient_evaluationOverviewSurveys_evaluationBWCSTitleTile:Échelle de Contrôle de l'Intestin (BWCS)`,
      order: 1,
      features: [
        {
          featureId: FeatureService.FEATURE_BWCS_SCR,
          title: $localize`:@@patient_evaluationOverviewSurveys_featureBWCSSCRTitle:Score`,
          label: $localize`:@@patient_evaluationOverviewSurveys_featureBWCSSCRLabel:Score`,
          min: 0,
          max: 26,
          chartDatasetOption: {
            type: 'bar',
            color: '#8893aa',
            nbSteps: 10,
            tooltip: {
              label: $localize`:@@patient_evaluationOverviewSurveys_featureBWCSSCRTooltipLabel:Score`,
              unit: '',
            },
          },
        },
      ],
      horizontalLimitLines: [
        {
          label: $localize`:@@patient_evaluationOverviewSurveys_featureHorizontalLimitLineYLabel:Maximum severity`,
          color: '#8893aa',
          assignToAxisIds: {
            y: 26,
          },
        },
      ],
    },
    2: <ChartEvaluation>{
      evaluationCode: EvaluationService.EVALUATION_MFIS5,
      title: $localize`:@@patient_evaluationOverviewSurveys_evaluationMFIS5Title:Modified Fatigue Impact Scale - 5 Item Version (MFIS-5)`,
      titleTile: $localize`:@@patient_evaluationOverviewSurveys_evaluationMFIS5TitleTile:Échelle Modifiée d'Impact de la Fatigue (MFIS-5)`,
      order: 2,
      features: [
        {
          featureId: FeatureService.FEATURE_MFIS5_SCR,
          title: $localize`:@@patient_evaluationOverviewSurveys_featureMFIS5SCRTitle:Score`,
          label: $localize`:@@patient_evaluationOverviewSurveys_featureMFIS5SCRLabel:Score`,
          min: 0,
          max: 20,
          chartDatasetOption: {
            type: 'bar',
            color: '#8893aa',
            nbSteps: 10,
            tooltip: {
              label: $localize`:@@patient_evaluationOverviewSurveys_featureMFIS5SCRTooltipLabel:Score`,
              unit: '',
            },
          },
        },
      ],
      horizontalLimitLines: [
        {
          label: $localize`:@@patient_evaluationOverviewSurveys_featureHorizontalLimitLineYLabel:Maximum severity`,
          color: '#8893aa',
          assignToAxisIds: {
            y: 20,
          },
        },
      ],
    },
    3: <ChartEvaluation>{
      evaluationCode: EvaluationService.EVALUATION_PHQ8,
      title: $localize`:@@patient_evaluationOverviewSurveys_evaluationPHQ8Title:Patient Health Questionnaire–8 (PHQ-8)`,
      titleTile: $localize`:@@patient_evaluationOverviewSurveys_evaluationPHQ8TitleTile:Échelle de Dépression (PHQ8)`,
      order: 3,
      features: [
        {
          featureId: FeatureService.FEATURE_PHQ8_SCR,
          title: $localize`:@@patient_evaluationOverviewSurveys_featurePHQ8SCRTitle:Score`,
          label: $localize`:@@patient_evaluationOverviewSurveys_featurePHQ8SCRLabel:Score`,
          min: 0,
          max: 24,
          chartDatasetOption: {
            type: 'bar',
            color: '#8893aa',
            nbSteps: 10,
            tooltip: {
              label: $localize`:@@patient_evaluationOverviewSurveys_featurePHQ8SCRTooltipLabel:Score`,
              unit: '',
            },
          },
        },
      ],
      horizontalLimitLines: [
        {
          label: $localize`:@@patient_evaluationOverviewSurveys_featureHorizontalLimitLineYLabel:Maximum severity`,
          color: '#8893aa',
          assignToAxisIds: {
            y: 24,
          },
        },
      ],
    },
    4: <ChartEvaluation>{
      evaluationCode: EvaluationService.EVALUATION_SF36,
      title: $localize`:@@patient_evaluationOverviewSurveys_evaluationSF36Title:36-Item Abbreviated Survey Instrument (SF-36)`,
      titleTile: $localize`:@@patient_evaluationOverviewSurveys_evaluationSF36TitleTile:Échelle de Qualité de Vie (SF36)`,
      order: 4,
      features: [
        {
          featureId: FeatureService.FEATURE_SF36_PHYSICAL_HEALTH_SCR,
          title: $localize`:@@patient_evaluationOverviewSurveys_featureSF36PhysicalHealthSCRTitle:PCS`,
          label: $localize`:@@patient_evaluationOverviewSurveys_featureSF36PhysicalHealthSCRLabel:Average Physical Score (PCS)`,
          min: 0,
          max: 100,
          chartDatasetOption: {
            type: 'bar',
            color: '#8893aa',
            nbSteps: 10,
            tooltip: {
              label: $localize`:@@patient_evaluationOverviewSurveys_featureSF36PhysicalHealthSCRTooltipLabel:Score`,
              unit: '',
            },
          },
        },
        {
          featureId: FeatureService.FEATURE_SF36_MENTAL_HEALTH_SCR,
          title: $localize`:@@patient_evaluationOverviewSurveys_featureSF36MentalHealthSCRTitle:MCS`,
          label: $localize`:@@patient_evaluationOverviewSurveys_featureSF36MentalHealthSCRLabel:Mean Mental Score (MCS)`,
          min: 0,
          max: 100,
          chartDatasetOption: {
            type: 'bar',
            color: '#0dc49c',
            nbSteps: 10,
            tooltip: {
              label: $localize`:@@patient_evaluationOverviewSurveys_featureSF36MentalHealthSCRTooltipLabel:Score`,
              unit: '',
            },
            axis: {
              id: 'sf362scr',
              type: 'linear',
              position: 'right',
              beginAtZero: true,
            },
          },
        },
      ],
      horizontalLimitLines: [
        {
          label: $localize`:@@patient_evaluationOverviewSurveys_featureSF36PhysicalHealthHorizontalLimitLineYLabel:Maximum health level`,
          color: '#3d3d40',
          assignToAxisIds: {
            y: 100,
          },
        },
      ],
    },
    5: <ChartEvaluation>{
      evaluationCode: EvaluationService.EVALUATION_PS,
      title: $localize`:@@patient_evaluationOverviewSurveys_evaluationPSTitle:Pain Scale (PS)`,
      titleTile: $localize`:@@patient_evaluationOverviewSurveys_evaluationPSTitleTile:Échelle de la Douleur (PS)`,
      order: 5,
      features: [
        {
          featureId: FeatureService.FEATURE_PS_SCR,
          title: $localize`:@@patient_evaluationOverviewSurveys_featurePSSCRTitle:Score`,
          label: $localize`:@@patient_evaluationOverviewSurveys_featurePSSCRLabel:Score`,
          min: 0,
          max: 100,
          chartDatasetOption: {
            type: 'bar',
            color: '#8893aa',
            nbSteps: 10,
            tooltip: {
              label: $localize`:@@patient_evaluationOverviewSurveys_featurePSSCRTooltipLabel:Score`,
              unit: '',
            },
          },
        },
      ],
      horizontalLimitLines: [
        {
          label: $localize`:@@patient_evaluationOverviewSurveys_featureHorizontalLimitLineYLabel:Maximum severity`,
          color: '#8893aa',
          assignToAxisIds: {
            y: 100,
          },
        },
      ],
    },
  };
  public datasets: any = {};
  public xLabels: any = {};
  public loading = true;
  @Output() emitEvaluationCode = new EventEmitter<string>();

  constructor(
    private featureService: FeatureService,
    private chartService: ChartService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.setOverviewCharts();
    this.evaluations = orderBy(this.evaluations, ['order']);
  }

  /**
   * Sets overview charts
   */
  setOverviewCharts(): void {
    let sources$ = {};

    for (const [key, evaluation] of Object.entries(this.evaluations)) {
      this.xLabels[key] = [] as ChartLabel[];
      this.datasets[key] = [] as ChartDataset[];
      this._features[key] = [] as Feature[];

      let params = {
        page: 1,
        startDate: moment()
          .subtract(12, 'months')
          .utc(true)
          .startOf('month')
          .toISOString(),
        endDate: moment().utc(true).endOf('month').toISOString(),
        patientUuid: this.route.snapshot.params['uuid'],
        featureIds: evaluation.features.map((item) => item.featureId),
      };

      sources$[key] = this.featureService.getFeatures(params).pipe(
        expand((res) => {
          params.page++;
          this._features[key] = this._features[key].concat(res);

          return res.length < 50
            ? EMPTY
            : this.featureService.getFeatures(params);
        })
      );
    }

    forkJoin(sources$)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res) => {
          for (const [key, value] of Object.entries(this._features)) {
            let chartDataPoints = {};

            this.evaluations[key].features.forEach((feature) => {
              let groupedFeatures = this.featureService.groupFeatures(
                value as Feature[],
                feature.featureId
              );
              chartDataPoints[feature.featureId] =
                this.chartService.setChartDataPoints(groupedFeatures);
            });

            if (Object.values(chartDataPoints).length > 0) {
              // Set labels
              const maxDataPoints = Object.values(chartDataPoints).reduce(
                (r: any, s: any) => (r.length > s.length ? r : s),
                0
              );
              this.xLabels[key] = this.chartService.setXLabels(
                maxDataPoints as ChartDataPoint[]
              );

              // Set datasets
              this.evaluations[key].features.forEach((feature, j) => {
                const dataset = this.chartService.setDataset(
                  feature,
                  chartDataPoints[feature.featureId],
                  feature.chartDatasetOption,
                  [],
                  this.xLabels[key],
                  feature.min ??
                    (chartDataPoints[feature.featureId].length
                      ? chartDataPoints[feature.featureId][0].min
                      : null),
                  feature.max ??
                    (chartDataPoints[feature.featureId].length
                      ? chartDataPoints[feature.featureId][0].max
                      : null),
                  feature.isMaxLimited,
                  feature.isMinLimited
                );

                this.datasets[key].push(dataset);
                feature['numberTotalEval'] =
                  chartDataPoints[feature.featureId].length;
                feature['progression'] = this.chartService.getProgression(
                  chartDataPoints[feature.featureId],
                  true
                );
              });
            }
          }
        },
      });
  }

  sendCodeToOverview(evaluationCode: string) {
    this.emitEvaluationCode.emit(evaluationCode);
  }
}
